import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { FolderPermissionUserEntry, PermissionLevel } from '../api/types';

type DropdownValue = 'NONE' | PermissionLevel;

export function PermissionsModal({ folderId, onClose }: { folderId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<FolderPermissionUserEntry[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .get<FolderPermissionUserEntry[]>(`/folders/${folderId}/permissions/users`)
      .then(setUsers)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [folderId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(q) || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q),
    );
  }, [users, query]);

  const handleChange = async (userId: string, value: DropdownValue) => {
    setPendingId(userId);
    setError(null);
    try {
      if (value === 'NONE') {
        await api.delete(`/folders/${folderId}/permissions/${userId}`);
      } else {
        await api.put(`/folders/${folderId}/permissions/${userId}`, { level: value });
      }
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, level: value === 'NONE' ? null : value } : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal card" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('permissions.manageTitle')}</h3>
          <button className="btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        <input
          type="search"
          placeholder={t('permissions.search') ?? ''}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%', marginBottom: 12, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
        />

        {error && <p className="error-text">{error}</p>}

        {loading ? (
          <p>{t('common.loading')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('users.title')}</th>
                <th>{t('folders.permissions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.firstName} {u.lastName}
                    <br />
                    <span className="badge">{u.email}</span>
                  </td>
                  <td>
                    <select
                      value={u.level ?? 'NONE'}
                      disabled={pendingId === u.id}
                      onChange={(e) => handleChange(u.id, e.target.value as DropdownValue)}
                    >
                      <option value="NONE">{t('permissions.level.NONE')}</option>
                      <option value="READ">{t('permissions.level.READ')}</option>
                      <option value="WRITE">{t('permissions.level.WRITE')}</option>
                      <option value="ADMIN">{t('permissions.level.ADMIN')}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
